import { useState, useEffect, useCallback, useRef } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { getCellValue } from "@/components/tableau";
import { getColumnKey, resolveActualKey } from "@/components/tableau/utils/formatters";

export function useCellSelection({ orders, setOrders, columns, markRowAsModified, filteredOrders, pushUndoAction }) {
  const [selectedCells, setSelectedCells] = useState([]);
  const [copiedCells, setCopiedCells] = useState([]);
  const [selectionAnchor, setSelectionAnchor] = useState(null);
  const [isDragFilling, setIsDragFilling] = useState(false);
  const [dragFillRange, setDragFillRange] = useState([]);
  const dragFillSourceRef = useRef(null);

  // Save a cell value to the backend
  const saveCellValue = useCallback(async (orderId, columnName, value) => {
    const order = orders.find(o => o.order_id === orderId);
    if (!order) return false;
    const key = resolveActualKey(order.extracted_data || {}, columnName);
    const updatedData = { ...order.extracted_data, [key]: value };
    try {
      const response = await authFetch(`${API}/orders/${orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: orderId, extracted_data: updatedData }),
      });
      if (response.ok) {
        setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, extracted_data: updatedData } : o));
        markRowAsModified(orderId);
        return true;
      }
    } catch (e) {
      console.error("Save cell failed:", e);
    }
    return false;
  }, [orders, setOrders, markRowAsModified]);

  // Select cell(s) on click
  const handleCellSelect = useCallback((orderId, columnName, rowIdx, colIdx, event) => {
    if (event.ctrlKey || event.metaKey) {
      // Toggle cell in selection
      setSelectedCells(prev => {
        const exists = prev.some(c => c.orderId === orderId && c.columnName === columnName);
        if (exists) return prev.filter(c => !(c.orderId === orderId && c.columnName === columnName));
        return [...prev, { orderId, columnName, rowIdx, colIdx }];
      });
    } else if (event.shiftKey && selectionAnchor) {
      // Range select from anchor to current
      const minRow = Math.min(selectionAnchor.rowIdx, rowIdx);
      const maxRow = Math.max(selectionAnchor.rowIdx, rowIdx);
      const minCol = Math.min(selectionAnchor.colIdx, colIdx);
      const maxCol = Math.max(selectionAnchor.colIdx, colIdx);
      const range = [];
      for (let r = minRow; r <= maxRow; r++) {
        const order = filteredOrders[r];
        if (!order) continue;
        for (let c = minCol; c <= maxCol; c++) {
          const col = columns[c];
          if (col) range.push({ orderId: order.order_id, columnName: col.name, rowIdx: r, colIdx: c });
        }
      }
      setSelectedCells(range);
    } else {
      setSelectedCells([{ orderId, columnName, rowIdx, colIdx }]);
      setSelectionAnchor({ rowIdx, colIdx });
    }
  }, [selectionAnchor, filteredOrders, columns]);

  // Clear selection on escape or outside click
  const clearSelection = useCallback(() => {
    setSelectedCells([]);
    setCopiedCells([]);
  }, []);

  // Copy selected cells
  const handleCopy = useCallback(() => {
    if (!selectedCells.length) return;
    const copied = selectedCells.map(cell => {
      const order = orders.find(o => o.order_id === cell.orderId);
      const col = columns.find(c => c.name === cell.columnName);
      const value = order && col ? getCellValue(order, col) : "";
      return { ...cell, value: value || "" };
    });
    setCopiedCells(copied);

    // Also copy to system clipboard (plain text)
    if (copied.length === 1) {
      navigator.clipboard?.writeText(String(copied[0].value || "")).catch(() => {});
    } else {
      // Multi-cell: build tab-separated text
      const rows = {};
      copied.forEach(c => {
        if (!rows[c.rowIdx]) rows[c.rowIdx] = {};
        rows[c.rowIdx][c.colIdx] = c.value;
      });
      const sortedRowIdxs = Object.keys(rows).map(Number).sort((a, b) => a - b);
      const allColIdxs = [...new Set(copied.map(c => c.colIdx))].sort((a, b) => a - b);
      const text = sortedRowIdxs.map(r => allColIdxs.map(c => rows[r]?.[c] || "").join("\t")).join("\n");
      navigator.clipboard?.writeText(text).catch(() => {});
    }

    toast.success(`${copied.length} cellule${copied.length > 1 ? "s" : ""} copiée${copied.length > 1 ? "s" : ""}`);
  }, [selectedCells, orders, columns]);

  // Paste copied cells to selected target(s)
  const handlePaste = useCallback(async () => {
    if (!copiedCells.length || !selectedCells.length) return;

    let count = 0;
    const undoActions = [];

    if (copiedCells.length === 1) {
      const value = copiedCells[0].value;
      for (const target of selectedCells) {
        const order = orders.find(o => o.order_id === target.orderId);
        const col = columns.find(c => c.name === target.columnName);
        const oldValue = order && col ? getCellValue(order, col) : "";
        const ok = await saveCellValue(target.orderId, target.columnName, value);
        if (ok) {
          undoActions.push({ orderId: target.orderId, columnName: target.columnName, oldValue: oldValue || "", newValue: value });
          count++;
        }
      }
    } else {
      const srcMinRow = Math.min(...copiedCells.map(c => c.rowIdx));
      const srcMinCol = Math.min(...copiedCells.map(c => c.colIdx));
      const tgtMinRow = Math.min(...selectedCells.map(c => c.rowIdx));
      const tgtMinCol = Math.min(...selectedCells.map(c => c.colIdx));

      for (const src of copiedCells) {
        const targetRowIdx = tgtMinRow + (src.rowIdx - srcMinRow);
        const targetColIdx = tgtMinCol + (src.colIdx - srcMinCol);
        const targetOrder = filteredOrders[targetRowIdx];
        const targetCol = columns[targetColIdx];
        if (targetOrder && targetCol) {
          const existingOrder = orders.find(o => o.order_id === targetOrder.order_id);
          const oldValue = existingOrder ? getCellValue(existingOrder, targetCol) : "";
          const ok = await saveCellValue(targetOrder.order_id, targetCol.name, src.value);
          if (ok) {
            undoActions.push({ orderId: targetOrder.order_id, columnName: targetCol.name, oldValue: oldValue || "", newValue: src.value });
            count++;
          }
        }
      }
    }

    if (count > 0) {
      if (pushUndoAction && undoActions.length > 0) pushUndoAction(undoActions);
      toast.success(`${count} cellule${count > 1 ? "s" : ""} collée${count > 1 ? "s" : ""}`);
    }
  }, [copiedCells, selectedCells, filteredOrders, columns, orders, saveCellValue, pushUndoAction]);

  // Delete selected cells
  const handleDeleteSelected = useCallback(async () => {
    if (!selectedCells.length) return;
    let count = 0;
    const undoActions = [];
    for (const cell of selectedCells) {
      const order = orders.find(o => o.order_id === cell.orderId);
      const col = columns.find(c => c.name === cell.columnName);
      const oldValue = order && col ? getCellValue(order, col) : "";
      const ok = await saveCellValue(cell.orderId, cell.columnName, "");
      if (ok) {
        undoActions.push({ orderId: cell.orderId, columnName: cell.columnName, oldValue: oldValue || "", newValue: "" });
        count++;
      }
    }
    if (count > 0) {
      if (pushUndoAction && undoActions.length > 0) pushUndoAction(undoActions);
      toast.success(`${count} cellule${count > 1 ? "s" : ""} effacée${count > 1 ? "s" : ""}`);
    }
  }, [selectedCells, orders, columns, saveCellValue, pushUndoAction]);

  // Drag-fill: start
  const handleDragFillStart = useCallback((e, orderId, columnName, rowIdx, colIdx) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragFilling(true);
    const order = orders.find(o => o.order_id === orderId);
    const col = columns.find(c => c.name === columnName);
    const value = order && col ? getCellValue(order, col) : "";
    dragFillSourceRef.current = { orderId, columnName, rowIdx, colIdx, value: value || "" };
    setDragFillRange([]);
  }, [orders, columns]);

  // Drag-fill: move (called from cell onMouseEnter during drag)
  const handleDragFillMove = useCallback((rowIdx) => {
    if (!isDragFilling || !dragFillSourceRef.current) return;
    const srcRow = dragFillSourceRef.current.rowIdx;
    const colIdx = dragFillSourceRef.current.colIdx;
    const minRow = Math.min(srcRow, rowIdx);
    const maxRow = Math.max(srcRow, rowIdx);
    const range = [];
    for (let r = minRow; r <= maxRow; r++) {
      if (r === srcRow) continue;
      const order = filteredOrders[r];
      const col = columns[colIdx];
      if (order && col) range.push({ orderId: order.order_id, columnName: col.name, rowIdx: r, colIdx });
    }
    setDragFillRange(range);
  }, [isDragFilling, filteredOrders, columns]);

  // Drag-fill: end
  const handleDragFillEnd = useCallback(async () => {
    if (!isDragFilling || !dragFillSourceRef.current || !dragFillRange.length) {
      setIsDragFilling(false);
      setDragFillRange([]);
      dragFillSourceRef.current = null;
      return;
    }

    const value = dragFillSourceRef.current.value;
    let count = 0;
    const undoActions = [];
    for (const target of dragFillRange) {
      const order = orders.find(o => o.order_id === target.orderId);
      const col = columns.find(c => c.name === target.columnName);
      const oldValue = order && col ? getCellValue(order, col) : "";
      const ok = await saveCellValue(target.orderId, target.columnName, value);
      if (ok) {
        undoActions.push({ orderId: target.orderId, columnName: target.columnName, oldValue: oldValue || "", newValue: value });
        count++;
      }
    }

    if (count > 0) {
      if (pushUndoAction && undoActions.length > 0) pushUndoAction(undoActions);
      toast.success(`${count} cellule${count > 1 ? "s" : ""} remplie${count > 1 ? "s" : ""}`);
    }
    setIsDragFilling(false);
    setDragFillRange([]);
    dragFillSourceRef.current = null;
  }, [isDragFilling, dragFillRange, orders, columns, saveCellValue, pushUndoAction]);

  // Check functions for styling
  const isCellSelected = useCallback((orderId, columnName) => {
    return selectedCells.some(c => c.orderId === orderId && c.columnName === columnName);
  }, [selectedCells]);

  const isCellCopied = useCallback((orderId, columnName) => {
    return copiedCells.some(c => c.orderId === orderId && c.columnName === columnName);
  }, [copiedCells]);

  const isCellInDragFill = useCallback((orderId, columnName) => {
    return dragFillRange.some(c => c.orderId === orderId && c.columnName === columnName);
  }, [dragFillRange]);

  // Get the last selected cell (for drag-fill handle position)
  const lastSelectedCell = selectedCells.length === 1 ? selectedCells[0] : null;

  // Keyboard handler
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;

      if ((e.ctrlKey || e.metaKey) && e.key === "c") {
        if (selectedCells.length > 0) { e.preventDefault(); handleCopy(); }
      } else if ((e.ctrlKey || e.metaKey) && e.key === "v") {
        if (copiedCells.length > 0 && selectedCells.length > 0) { e.preventDefault(); handlePaste(); }
      } else if (e.key === "Escape") {
        clearSelection();
      } else if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedCells.length > 0) { e.preventDefault(); handleDeleteSelected(); }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [selectedCells, copiedCells, handleCopy, handlePaste, handleDeleteSelected, clearSelection]);

  // Mouse up listener for drag-fill
  useEffect(() => {
    if (!isDragFilling) return;
    const handleMouseUp = () => handleDragFillEnd();
    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [isDragFilling, handleDragFillEnd]);

  return {
    selectedCells, copiedCells, isDragFilling, dragFillRange, lastSelectedCell,
    handleCellSelect, clearSelection,
    handleCopy, handlePaste, handleDeleteSelected,
    handleDragFillStart, handleDragFillMove, handleDragFillEnd,
    isCellSelected, isCellCopied, isCellInDragFill,
  };
}
